import type { Proposal, ProposalReadiness } from './types.js';

const REQUIRED_FIELDS: Array<{ label: string; valid: (proposal: Proposal) => boolean }> = [
  { label: 'Proposal Name', valid: (proposal) => hasText(proposal.proposalName) },
  { label: 'Proposal Type', valid: (proposal) => hasText(proposal.proposalType) },
  { label: 'Status', valid: (proposal) => hasText(proposal.status) },
  { label: 'Portal Visibility', valid: (proposal) => hasText(proposal.portalVisibility) },
  {
    label: 'Proposal Amount greater than 0',
    valid: (proposal) =>
      typeof proposal.proposalAmount === 'number' && proposal.proposalAmount > 0,
  },
  { label: 'Scope Summary', valid: (proposal) => hasText(proposal.scopeSummary) },
  { label: 'Deliverables', valid: (proposal) => hasText(proposal.deliverables) },
  { label: 'Timeline', valid: (proposal) => hasText(proposal.timeline) },
  { label: 'Payment Terms', valid: (proposal) => hasText(proposal.paymentTerms) },
  {
    label: 'Client-Facing Next Step',
    valid: (proposal) => hasText(proposal.clientFacingNextStep),
  },
  { label: 'Valid Until', valid: (proposal) => hasText(proposal.validUntil) },
];

export function getProposalReadiness(proposal: Proposal): ProposalReadiness {
  const missing = REQUIRED_FIELDS.filter((field) => !field.valid(proposal)).map(
    (field) => field.label,
  );
  const warnings: string[] = [];

  if (!hasText(proposal.generatedPdfUrl)) warnings.push('No Generated PDF URL');
  if (!hasText(proposal.generatedDocUrl)) warnings.push('No Generated Doc URL');
  if (!proposal.proposalDocument || proposal.proposalDocument.length === 0) {
    warnings.push('No Proposal Document attachment');
  }
  if (proposal.validUntil && isPastDate(proposal.validUntil)) {
    warnings.push('Valid Until is in the past');
  }
  if (proposal.status === 'Draft' && proposal.portalVisibility === 'Client Visible') {
    warnings.push('Status is Draft but Portal Visibility is Client Visible');
  }

  const completed = REQUIRED_FIELDS.length - missing.length;
  const score = Math.round((completed / REQUIRED_FIELDS.length) * 100);
  return { score, missing, warnings };
}

export function getProposalReadinessLabel(proposal: Proposal): 'Needs Pricing' | 'Ready to Generate' | 'Ready to Send' | 'Draft' {
  const readiness = getProposalReadiness(proposal);
  if (!proposal.proposalAmount || proposal.proposalAmount <= 0) return 'Needs Pricing';
  if (!proposal.generatedPdfUrl && !proposal.generatedDocUrl && readiness.missing.length === 0) {
    return 'Ready to Generate';
  }
  if (
    readiness.missing.length === 0 &&
    (hasText(proposal.generatedPdfUrl) || hasText(proposal.generatedDocUrl))
  ) {
    return 'Ready to Send';
  }
  return 'Draft';
}

function hasText(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function isPastDate(value: string): boolean {
  const dateOnly = value.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return false;
  const today = new Date();
  const todayOnly = new Date(
    Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()),
  );
  const candidate = new Date(`${dateOnly}T00:00:00.000Z`);
  return candidate.getTime() < todayOnly.getTime();
}
